/**
 * FIX 8.9j-P1 · Toggle "mostra/nascondi giacenze numeriche" (staff/admin).
 *
 * Il cliente ricorda che "una volta" c'era un'opzione per nascondere le
 * giacenze nella vista staff — utile durante il servizio quando si mostra
 * la carta al tavolo senza dare visibilità dei numeri esatti al cliente
 * seduto accanto.
 *
 * Hook + toggle side-by-side alla label "VISTA STAFF · GIACENZE VISIBILI"
 * di Catalog / Spirits. La preferenza è persistita in localStorage con
 * chiave dedicata `co_staff_show_stock` (default `true`).
 *
 * SICUREZZA · questo toggle è PURA PRESENTAZIONE. Le API server-side
 * escludono `stock_quantity` per ruoli non staff/admin a livello di
 * serializer (`serialize_wine` in backend/models.py). Il toggle
 * disabilita solo la resa visiva quando l'utente ha già i dati numerici.
 */
import { useCallback, useEffect, useState } from "react";
import { EyeOff, Eye } from "lucide-react";

const STORAGE_KEY = "co_staff_show_stock";

function readInitial() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw === null) return true;   // default: mostra
        return raw !== "false";
    } catch (e) {
        return true;
    }
}

export function useStaffStockVisibility() {
    const [showStock, setShowStock] = useState(readInitial);

    // Persist + sync tra tab (storage event).
    useEffect(() => {
        try { localStorage.setItem(STORAGE_KEY, showStock ? "true" : "false"); } catch (e) {}
    }, [showStock]);

    useEffect(() => {
        const onStorage = (e) => {
            if (e.key === STORAGE_KEY) setShowStock(e.newValue !== "false");
        };
        window.addEventListener("storage", onStorage);
        return () => window.removeEventListener("storage", onStorage);
    }, []);

    const toggle = useCallback(() => setShowStock((v) => !v), []);
    return { showStock, setShowStock, toggle };
}


/**
 * Pulsante inline "Mostra/Nascondi giacenze". Renderizzato solo per
 * staff/admin. Design: text-only con icona, non invasivo, coerente col
 * `co-caption` del contesto.
 */
export function StaffStockToggle({ showStock, onToggle, className = "" }) {
    const label = showStock ? "Nascondi giacenze" : "Mostra giacenze";
    const Icon = showStock ? EyeOff : Eye;
    return (
        <button
            type="button"
            onClick={onToggle}
            data-testid="staff-stock-toggle"
            aria-pressed={!showStock}
            title={label}
            className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[hsl(var(--primary))] hover:opacity-80 transition-opacity ${className}`}
        >
            <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
            {label}
        </button>
    );
}
